import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLang } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300";

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useLang();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(t('auth.min6')); return; }
    if (password !== confirm) { toast.error(t('auth.mismatch')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      toast.success(t('auth.updated'));
      navigate('/login');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? t(d) : t('auth.reset_fail'));
    }
    setLoading(false);
  };

  return (
    <div className="pt-20 min-h-screen flex items-center" data-testid="reset-password-page">
      <div className="max-w-md mx-auto px-6 py-24 w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-10">
            <Lock className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="text-3xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB] mb-2">{t('auth.new_pass_title')}</h1>
            <p className="text-sm text-[#A3A19C]">{t('auth.new_pass_sub')}</p>
          </div>
          {!token ? (
            <p className="text-center text-sm text-[#A3A19C]" data-testid="reset-password-missing-token">
              {t('auth.invalid_link')} <Link to="/forgot-password" className="text-[#D4AF37]">{t('auth.request_new')}</Link>.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
              <div>
                <label className="block text-sm text-[#A3A19C] mb-2">{t('auth.new_pass')}</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} className={inputCls} placeholder={t('auth.ph_min6')} data-testid="reset-password-input" />
              </div>
              <div>
                <label className="block text-sm text-[#A3A19C] mb-2">{t('auth.confirm_pass')}</label>
                <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className={inputCls} placeholder={t('auth.ph_repeat')} data-testid="reset-password-confirm" />
              </div>
              <button type="submit" disabled={loading} className="w-full btn-primary" data-testid="reset-password-submit">
                {loading ? t('auth.updating') : t('auth.update_pass')}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
